import {
  CommonWhenType,
  NylasCalendarEvent,
} from '../../../../../generated/graphql';
import { logger } from '../../../../../lib/logger';
import { combineDateTime } from './dateTimeUtils';

interface EventTimeData {
  when: {
    startDate: string;
    endDate: string;
    startTime?: string;
    endTime?: string;
    startTimezone?: string;
    endTimezone?: string;
  };
  isAllDay?: boolean;
  timezone?: string;
}

interface EventFormData extends EventTimeData {
  title?: string;
  description?: string;
  location?: string;
  participants?: Array<any>;
}

export const hasDirtyFields = (
  dirtyFields: Record<string, any>,
  fields: string[],
): boolean => {
  return fields.some((field) => {
    // Handle nested fields (e.g., 'when.startDate')
    const parts = field.split('.');
    let current = dirtyFields;

    for (const part of parts) {
      if (!current[part]) return false;
      current = current[part];
    }

    return true;
  });
};

export const serializeEventTime = (
  data: EventTimeData,
): Partial<NylasCalendarEvent> => {
  const serialized: Partial<NylasCalendarEvent> = {};

  logger.debug('🎯 serializeEventTime', data);

  if (data.isAllDay) {
    // For all-day events, just use YYYY-MM-DD format
    const startDate = data.when.startDate;
    const endDate = data.when.endDate;

    serialized.when = {
      type: CommonWhenType.Datespan,
      startDate,
      endDate,
    };
  } else {
    // Combine date and time into ISO8601 strings using the specified timezones
    const startDate = data.when.startDate;
    const endDate = data.when.endDate || startDate;

    const startDateTime = combineDateTime(
      startDate,
      data.when.startTime || '00:00',
      data.when.startTimezone || data.timezone,
    );
    const endDateTime = combineDateTime(
      endDate,
      data.when.endTime || '00:00',
      data.when.endTimezone || data.timezone,
    );

    serialized.when = {
      type: CommonWhenType.Timespan,
      startTime: startDateTime,
      endTime: endDateTime,
      startTimezone: data.when.startTimezone || data.timezone,
      endTimezone: data.when.endTimezone || data.timezone,
    };
  }

  return serialized;
};

export const serializeEventUpdate = (
  data: EventFormData,
  dirtyFields: Record<string, any>,
): Partial<NylasCalendarEvent> => {
  const serialized: Partial<NylasCalendarEvent> = {};

  // Handle non-time fields
  if (dirtyFields.title) serialized.title = data.title;
  if (dirtyFields.description) serialized.description = data.description;
  if (dirtyFields.location) serialized.location = data.location;
  if (dirtyFields.participants) serialized.participants = data.participants;

  // Handle time fields if modified
  const timeFieldsChanged = hasDirtyFields(dirtyFields, [
    'isAllDay',
    'when.startDate',
    'when.endDate',
    'when.startTime',
    'when.endTime',
    'when.startTimezone',
    'when.endTimezone',
    'timezone',
  ]);

  if (timeFieldsChanged) {
    const timeData = serializeEventTime(data);
    serialized.when = timeData.when;
  }

  return serialized;
};
